import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
  getTemplateMetadata,
  getTemplatePreview,
  promoteTemplateDraft,
  adoptBundledTemplate,
  discardTemplateDraft,
  sendTemplateTestEmail,
  getEmailSchedulePortcos,
  isDemoMode,
  TemplateMetadata,
  SchedulePortcoOption,
  EmailPreviewData,
} from "../utils/PowerBIApiCall";
import { Loading } from "../ui-components/Loading";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Main content
// ---------------------------------------------------------------------------

function EmailTemplatesContent() {
  const [metadata, setMetadata] = useState<TemplateMetadata | null>(null);
  const [portcos, setPortcos] = useState<SchedulePortcoOption[]>([]);
  const [selectedPortco, setSelectedPortco] = useState("");
  const [variant, setVariant] = useState<"production" | "draft">("production");
  const [preview, setPreview] = useState<EmailPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [adopting, setAdopting] = useState(false);

  // Promote dialog
  const [showPromote, setShowPromote] = useState(false);
  const [promoteNotes, setPromoteNotes] = useState("");
  const [promoting, setPromoting] = useState(false);

  // Test email dialog
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // History
  const [showHistory, setShowHistory] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Load metadata + portcos
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [meta, portcoList] = await Promise.all([
        getTemplateMetadata(),
        getEmailSchedulePortcos(),
      ]);
      setMetadata(meta);
      setPortcos(portcoList);
      if (!selectedPortco && portcoList.length > 0) {
        setSelectedPortco(portcoList[0].id);
      }
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "Failed to load template data";
      setLoadError(msg);
      setActionError(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedPortco]);

  useEffect(() => {
    loadData();
  }, []);

  // Load preview when portco or variant changes
  const loadPreview = useCallback(async () => {
    if (!selectedPortco) return;
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      const data = await getTemplatePreview(selectedPortco, variant);
      setPreview(data);
    } catch (e) {
      setPreviewError(
        e instanceof Error ? e.message : "Failed to load preview",
      );
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedPortco, variant]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // Write HTML into iframe
  useEffect(() => {
    if (preview?.html && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(preview.html);
        doc.close();
      }
    }
  }, [preview]);

  // Stage the build's template as a draft so it can be previewed and promoted.
  const handleAdoptBundled = async () => {
    setActionError(null);
    setAdopting(true);
    try {
      await adoptBundledTemplate();
      await loadData();
      // Jump straight to the draft — the point is to see what changed.
      setVariant("draft");
      setActionMsg(
        "Loaded this build's template as a draft. Compare it against production, then promote.",
      );
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : "Failed to load bundled template",
      );
    } finally {
      setAdopting(false);
    }
  };

  // Promote handler
  const handlePromote = async () => {
    try {
      setPromoting(true);
      setActionError(null);
      const result = await promoteTemplateDraft(promoteNotes || undefined);
      setActionMsg(`Promoted to v${result.version}`);
      setShowPromote(false);
      setPromoteNotes("");
      await loadData();
      // Refresh preview to show production
      setVariant("production");
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : "Failed to promote draft",
      );
    } finally {
      setPromoting(false);
    }
  };

  // Discard handler
  const handleDiscard = async () => {
    if (!window.confirm("Discard the current draft? This cannot be undone."))
      return;
    try {
      setActionError(null);
      await discardTemplateDraft();
      setActionMsg("Draft discarded");
      await loadData();
      setVariant("production");
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : "Failed to discard draft",
      );
    }
  };

  // Send test email handler
  const handleSendTest = async () => {
    if (!testEmail || !selectedPortco) return;
    try {
      setSendingTest(true);
      setActionError(null);
      const result = await sendTemplateTestEmail(
        selectedPortco,
        variant,
        testEmail,
      );
      setActionMsg(result.message);
      setShowTestEmail(false);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : "Failed to send test email",
      );
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-6 px-4">
        <Loading />
      </div>
    );
  }

  if (loadError && !metadata) {
    return (
      <div className="max-w-6xl mx-auto py-6 px-4">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-wmp-500 hover:text-wmp-600 text-sm">
            &larr; Admin
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Email Template</h1>
        </div>
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
          Failed to load template data: {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-wmp-500 hover:text-wmp-600 text-sm"
            >
              &larr; Admin
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Email Template</h1>
            {metadata && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                v{metadata.currentVersion}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Preview and promote email template changes
            <span className="mx-1.5 text-gray-300">|</span>
            <Link
              to="/admin/email-schedules"
              className="text-wmp-500 hover:text-wmp-600"
            >
              Schedules
            </Link>
            <span className="mx-1.5 text-gray-300">|</span>
            <Link
              to="/admin/email-history"
              className="text-wmp-500 hover:text-wmp-600"
            >
              History
            </Link>
          </p>
        </div>

        {/* Draft status badge */}
        {metadata && (
          <div className="text-right">
            {/* "In sync" here means draft == production. It must not claim
                that while the build carries a newer template than the live
                one, or the badge contradicts the banner above. */}
            {metadata.draftExists ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Draft pending
              </span>
            ) : metadata.bundledUpdateAvailable ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Update available
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                In sync
              </span>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Last promoted {formatDateTime(metadata.lastPromotedAt)}
            </p>
          </div>
        )}
      </div>

      {/* A newer template shipped in the build, but the live one is customized
          so the server left it alone. Without this the only trace is an App
          Service log line, and the repo change silently never goes out. */}
      {metadata?.bundledUpdateAvailable && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">
            This deployment ships a newer email template than the live one
          </p>
          <p className="mt-1 text-amber-800">
            The live template has been customized, so it was left untouched.
            Template changes made in the repo will not appear in sent email
            until they are promoted here. Load the build&rsquo;s template as a
            draft to compare it against production, then promote &mdash;
            promoting replaces the live template, so check first that nothing
            you need is only in the live copy.
          </p>
          <button
            onClick={handleAdoptBundled}
            disabled={adopting}
            className="mt-3 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
          >
            {adopting ? "Loading..." : "Load into draft"}
          </button>
        </div>
      )}

      {/* Status messages */}
      {actionMsg && (
        <div className="mb-4 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800 flex justify-between items-center">
          {actionMsg}
          <button
            onClick={() => setActionMsg(null)}
            className="text-emerald-600 hover:text-emerald-800"
          >
            Dismiss
          </button>
        </div>
      )}
      {actionError && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800 flex justify-between items-center">
          {actionError}
          <button
            onClick={() => setActionError(null)}
            className="text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Controls row */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        {/* Portco selector */}
        <label className="text-sm font-medium text-gray-700">
          Preview with:
          <select
            value={selectedPortco}
            onChange={(e) => setSelectedPortco(e.target.value)}
            className="ml-2 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            {portcos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </select>
        </label>

        {/* Variant toggle */}
        <div className="flex rounded-md shadow-sm">
          <button
            onClick={() => setVariant("production")}
            className={`px-4 py-1.5 text-sm font-medium rounded-l-md border ${
              variant === "production"
                ? "bg-wmp-500 text-white border-wmp-500"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            Production
          </button>
          <button
            onClick={() => setVariant("draft")}
            className={`px-4 py-1.5 text-sm font-medium rounded-r-md border-l-0 border ${
              variant === "draft"
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            Draft
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Send test email */}
          <button
            onClick={() => setShowTestEmail(true)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Send Test Email
          </button>

          {/* Promote */}
          {metadata?.draftExists && (
            <>
              <button
                onClick={handleDiscard}
                className="px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
              >
                Discard Draft
              </button>
              <button
                onClick={() => setShowPromote(true)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700"
              >
                Promote to Production
              </button>
            </>
          )}
        </div>
      </div>

      {/* Preview iframe */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        {previewLoading ? (
          <div className="flex items-center justify-center h-96">
            <Loading />
          </div>
        ) : previewError ? (
          <div className="flex items-center justify-center h-96 text-red-600 text-sm">
            {previewError}
          </div>
        ) : (
          <div>
            {preview && (
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 flex items-center justify-between">
                <span>
                  <strong>Subject:</strong> {preview.subject}
                </span>
                <span>
                  {preview.portcoName} &middot; {preview.reportDate}
                </span>
              </div>
            )}
            <iframe
              ref={iframeRef}
              title="Email Template Preview"
              className="w-full border-0"
              style={{ height: "700px" }}
              sandbox="allow-same-origin"
            />
          </div>
        )}
      </div>

      {/* Version history */}
      {metadata && metadata.promotionHistory.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm font-medium text-wmp-500 hover:text-wmp-600"
          >
            {showHistory ? "Hide" : "Show"} Version History (
            {metadata.promotionHistory.length} versions)
          </button>

          {showHistory && (
            <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Version
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Promoted At
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      By
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[...metadata.promotionHistory].reverse().map((rec) => (
                    <tr key={rec.version}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">
                        v{rec.version}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {formatDateTime(rec.promotedAt)}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {rec.promotedBy}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {rec.notes || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Promote dialog */}
      {showPromote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Promote Draft to Production
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This will replace the current production template. Scheduled
              emails will use the new template immediately.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={promoteNotes}
              onChange={(e) => setPromoteNotes(e.target.value)}
              placeholder="e.g., Updated header layout, added callout section..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPromote(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePromote}
                disabled={promoting}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
              >
                {promoting ? "Promoting..." : "Promote"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test email dialog */}
      {showTestEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Send Test Email ({variant})
            </h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipient email
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowTestEmail(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testEmail}
                className="px-4 py-2 text-sm font-medium text-white bg-wmp-500 rounded-md hover:bg-wmp-600 disabled:opacity-50"
              >
                {sendingTest ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function EmailTemplates() {
  const { isAdmin, loading } = useIsAdmin();

  if (loading) return <Loading />;

  // Demo mode: always show (useIsAdmin returns true for demo)
  if (!isAdmin && !isDemoMode()) return <Navigate to="/dashboard" replace />;

  return <EmailTemplatesContent />;
}
